import Controller from '@ember/controller';
import { inject as service } from '@ember/service';
import { action, computed } from '@ember/object';
import TransactionalMessageModel from 'cio/models/transactional-message';
import { DEFAULT_FORMAT } from 'cio/utils/date';
import { Periods, buildMetricGroupingOptions } from 'cio/utils/metrics/common';
import {
  LAST_30_DAYS_RANGE_PRESET_UTC,
  LAST_12_WEEKS_FROM_NOW_RANGE_PRESET_UTC,
  LAST_12_MONTHS_RANGE_PRESET,
} from 'cio/utils/workflow-metrics';
import moment from 'moment-timezone';
import AnalyticsService from 'cio/services/analytics';
import EnvironmentService from 'cio/services/environment';
import { TimeRange } from 'cio/utils/time';
import { Grouping } from 'cio/utils/metrics/resolution';

export default class TransactionalIndexController extends Controller {
  @service analytics!: AnalyticsService;
  @service environment!: EnvironmentService;

  queryParams = [
    {
      searchTerm: { as: 'search' },
      sortBy: { as: 'sort' },
      sortDescending: { as: 'desc' },
      page: { as: 'page' },
      grouping: { as: 'group' },
      startTime: { as: 'start_time' },
      endTime: { as: 'end_time' },
      tagIds: { as: 'tag_ids' },
    },
  ];

  searchTerm = '';
  page = 1;
  sortBy = 'updated';
  sortDescending = true;
  grouping = Grouping.Daily;
  startTime!: string;
  endTime!: string;
  tagIds: string[] = [];

  declare model: TransactionalMessageModel[];

  get maxDate() {
    return moment();
  }

  get groupings() {
    return buildMetricGroupingOptions(this.timeRange);
  }

  @computed('model.@each.isDefault')
  get nonDefaultMessages() {
    return this.model.filter((m: TransactionalMessageModel) => !m.isDefault);
  }

  @computed('startTime', 'endTime')
  get timeRange() {
    return {
      start: moment(this.startTime),
      end: moment(this.endTime),
    };
  }

  @action
  onSetSortDescending(value: boolean) {
    this.set('sortDescending', value);
  }

  @action
  reloadMessageModel() {
    this.send('reloadModel');
  }

  @action
  onGroupingChange(value: Grouping) {
    this.set('grouping', value);
    this.trackFiltersChange('grouping');
  }

  @action
  onRangeSelect({ date }: { date: TimeRange }) {
    this.setRange(date);
    this.trackFiltersChange('timeframe');
  }

  @action
  didChangeTagIds(tagIds: string[]) {
    this.set('tagIds', tagIds);
  }

  setDefaults() {
    if (!this.startTime || !this.endTime) {
      this.setRange(LAST_30_DAYS_RANGE_PRESET_UTC.getRange());
    }
  }

  get dateRangePresets() {
    let allTimePreset = {
      label: 'All time',
      value: Periods.ALL_TIME,
      getRange: () => ({
        start: moment.unix(this.environment.current.createdAt).utc().startOf('day'),
        end: moment().utc().endOf('day'),
      }),
    };

    return [
      allTimePreset,
      LAST_30_DAYS_RANGE_PRESET_UTC,
      LAST_12_WEEKS_FROM_NOW_RANGE_PRESET_UTC,
      LAST_12_MONTHS_RANGE_PRESET,
    ];
  }

  private setRange(range: TimeRange) {
    if (!range.end || !range.start) {
      return;
    }

    this.setProperties({
      startTime: range.start?.format(DEFAULT_FORMAT),
      endTime: range.end?.format(DEFAULT_FORMAT),
    });
  }

  private trackFiltersChange(editType: 'grouping' | 'timeframe') {
    this.analytics.track('transactional', 'timeframe_filters_changed', {
      timeRange: this.timeRange,
      groupBy: this.grouping,
      editType,
    });
  }
}

declare module '@ember/controller' {
  interface Registry {
    'transactional.index': TransactionalIndexController;
  }
}
